import { ChevronLeft, Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomFieldsBuilder, type FormFieldSchema } from '../../custom-fields';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';

export function ContactTypeEditPage() {
  const { workspaceId, orgId, id } = useParams<{
    workspaceId: string;
    orgId: string;
    id: string;
  }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const { data: contactType, isLoading: isQueryLoading } = trpc.contactTypes.getById.useQuery(
    { id: parseInt(id!, 10) },
    { enabled: !!id }
  );

  const isLoading = isQueryLoading || meQuery.isLoading;

  useEffect(() => {
    if (!isLoading && isReadOnly) {
      toast.error('You do not have permission to edit contact types.');
      navigate(`/org/${orgId}/workspace/${workspaceId}/settings/contact-types`);
    }
  }, [isLoading, isReadOnly, navigate, orgId, workspaceId]);

  const updateMutation = trpc.contactTypes.update.useMutation({
    onSuccess: () => {
      toast.success('Contact type updated successfully');
      utils.contactTypes.list.invalidate();
      utils.contactTypes.getById.invalidate({ id: parseInt(id!, 10) });
      navigate(`/org/${orgId}/workspace/${workspaceId}/settings/contact-types`);
    },
    onError: (error) => {
      toast.error(`Failed to update contact type: ${error.message}`);
    },
  });

  const handleSave = (label: string, fields: FormFieldSchema[]) => {
    if (isReadOnly) return;
    updateMutation.mutate({
      id: parseInt(id!, 10),
      label,
      customFieldsSchema: JSON.stringify(fields),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!contactType) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-muted-foreground">Contact type not found</p>
        <Button asChild>
          <Link to={`/org/${orgId}/workspace/${workspaceId}/settings/contact-types`}>
            Back to Contact Types
          </Link>
        </Button>
      </div>
    );
  }

  const initialFields = contactType.customFieldsSchema
    ? JSON.parse(contactType.customFieldsSchema)
    : [];

  return (
    <div className="flex flex-col h-full bg-slate-50/50 dark:bg-slate-950/50">
      <div className="flex items-center gap-4 px-8 py-4 border-b border-border/50 bg-background/50 backdrop-blur-sm sticky top-0 z-10">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/org/${orgId}/workspace/${workspaceId}/settings/contact-types`}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-lg font-semibold">Edit Contact Type</h1>
          <p className="text-xs text-muted-foreground italic">ID: {id}</p>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <CustomFieldsBuilder
          key={contactType.id}
          label={contactType.label}
          initialFields={initialFields}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
        />
      </div>
    </div>
  );
}
